import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { changePassword } from '../../lib/api';

export default function ChangePasswordModal({ onClose, onDone }) {
  const { colors } = useTheme();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    const onEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [onClose]);

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (next.length < 8) { setErr('New password must be at least 8 characters.'); return; }
    if (next !== confirm) { setErr('New password and confirmation do not match.'); return; }
    setBusy(true);
    try {
      await changePassword(current, next);
      onDone?.();
    } catch (ex) {
      setErr(ex?.detail || 'Could not change password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="change-password-modal"
      role="dialog" aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(10,10,10,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.surface, borderRadius: 18, maxWidth: 440, width: '100%',
          padding: 28, boxShadow: '0 40px 100px -30px rgba(0,0,0,0.3)', position: 'relative',
        }}
      >
        <button type="button" onClick={onClose} aria-label="Close" data-testid="change-password-close" style={{ position: 'absolute', top: 14, right: 14, background: 'transparent', border: 'none', cursor: 'pointer', color: colors.textMuted, padding: 6 }}>
          <X size={18} />
        </button>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: colors.text, letterSpacing: '-0.02em' }}>Change password</h3>
        <p style={{ marginTop: 6, color: colors.textMuted, fontSize: 13 }}>Pick something you don't use anywhere else.</p>

        <label style={labelStyle(colors)}>
          Current password
          <input type="password" required value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" data-testid="pw-current" style={inputStyle(colors)} />
        </label>
        <label style={labelStyle(colors)}>
          New password
          <input type="password" required minLength={8} value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" data-testid="pw-new" style={inputStyle(colors)} />
        </label>
        <label style={labelStyle(colors)}>
          Confirm new password
          <input type="password" required minLength={8} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" data-testid="pw-confirm" style={inputStyle(colors)} />
        </label>

        {err && (
          <div data-testid="pw-error" style={{ color: '#DC2626', background: '#FEF2F2', border: '1px solid #FECACA', padding: 10, borderRadius: 10, fontSize: 13, marginTop: 10 }}>
            {err}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ padding: '10px 16px', borderRadius: 10, border: `1px solid ${colors.border}`, background: 'transparent', color: colors.text, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancel</button>
          <button type="submit" disabled={busy} data-testid="pw-submit" style={{ padding: '10px 16px', borderRadius: 10, border: 'none', background: colors.accent, color: 'white', cursor: busy ? 'not-allowed' : 'pointer', fontWeight: 700, fontSize: 13, boxShadow: `0 10px 24px -14px ${colors.accent}` }}>
            {busy ? 'Updating…' : 'Update password'}
          </button>
        </div>
      </form>
    </div>
  );
}

const labelStyle = (colors) => ({ display: 'block', color: colors.text, fontSize: 13, fontWeight: 600, marginTop: 14 });
const inputStyle = (colors) => ({ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '10px 12px', borderRadius: 10, border: `1px solid ${colors.border}`, fontSize: 14, color: colors.text, background: colors.surface, outline: 'none' });
